import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  SelectChangeEvent,
  Stack,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { ApiService } from "../services/api";

interface SupplierStats {
  nif_emitente: string;
  emitente_nome: string;
  total_invoices: number;
  total_valor: number;
}

interface ExpenseStats {
  nif_emitente: string;
  emitente_nome: string;
  total_valor: number;
}

const COLORS = [
  "#0088FE",
  "#00C49F",
  "#FFBB28",
  "#FF8042",
  "#8884D8",
  "#82CA9D",
  "#FFC658",
  "#FF6B6B",
  "#4ECDC4",
  "#45B7D1",
];

const ChartsView: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;

  const [ano, setAno] = useState<number>(currentYear);
  const [mes, setMes] = useState<number | "">(currentMonth);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suppliersData, setSuppliersData] = useState<SupplierStats[]>([]);
  const [expensesData, setExpensesData] = useState<ExpenseStats[]>([]);

  // Gerar anos (últimos 5 anos)
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);
  const months = [
    { value: 1, label: "Janeiro" },
    { value: 2, label: "Fevereiro" },
    { value: 3, label: "Março" },
    { value: 4, label: "Abril" },
    { value: 5, label: "Maio" },
    { value: 6, label: "Junho" },
    { value: 7, label: "Julho" },
    { value: 8, label: "Agosto" },
    { value: 9, label: "Setembro" },
    { value: 10, label: "Outubro" },
    { value: 11, label: "Novembro" },
    { value: 12, label: "Dezembro" },
  ];

  useEffect(() => {
    loadChartData();
  }, [ano, mes]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadChartData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Construir data_inicio e data_fim
      let data_inicio: string | undefined;
      let data_fim: string | undefined;

      if (mes) {
        // Filtro por mês específico
        data_inicio = `${ano}-${String(mes).padStart(2, "0")}-01`;
        const lastDay = new Date(ano, Number(mes), 0).getDate();
        data_fim = `${ano}-${String(mes).padStart(2, "0")}-${lastDay}`;
      } else {
        // Filtro por ano inteiro
        data_inicio = `${ano}-01-01`;
        data_fim = `${ano}-12-31`;
      }

      // Buscar todas as faturas com paginação
      let allInvoices: any[] = [];
      let currentPage = 1;
      let totalPages = 1;

      do {
        const response = await ApiService.getInvoices({
          data_inicio,
          data_fim,
          page: currentPage,
          limit: 100, // Máximo permitido
        });

        if (response.success && response.data) {
          allInvoices = [...allInvoices, ...response.data];
          if (response.pagination) {
            totalPages = response.pagination.pages;
          }
        }

        currentPage++;
      } while (currentPage <= totalPages);

      if (allInvoices.length > 0) {
        const invoices = allInvoices;

        // Agregar por fornecedor (top 10 com mais faturas)
        const supplierMap = new Map<string, SupplierStats>();

        invoices.forEach((invoice) => {
          const nif = invoice.nif_emitente || "Desconhecido";
          const nome =
            invoice.emitente_nome_curto || invoice.emitente_nome || nif;

          if (!supplierMap.has(nif)) {
            supplierMap.set(nif, {
              nif_emitente: nif,
              emitente_nome: nome,
              total_invoices: 0,
              total_valor: 0,
            });
          }

          const stats = supplierMap.get(nif)!;
          stats.total_invoices += 1;
          // Garantir que só somamos valores numéricos válidos
          // O backend pode retornar valores como string ou número
          const valorNumerico =
            typeof invoice.valor === "string"
              ? parseFloat(invoice.valor)
              : invoice.valor;
          const valorInvoice =
            typeof valorNumerico === "number" && !isNaN(valorNumerico)
              ? valorNumerico
              : 0;
          stats.total_valor += valorInvoice;
        });

        const suppliersArray = Array.from(supplierMap.values())
          .sort((a, b) => b.total_invoices - a.total_invoices)
          .slice(0, 10);

        setSuppliersData(suppliersArray);

        // Agregar despesas (top 10 maiores valores)
        const expensesArray = Array.from(supplierMap.values())
          .sort((a, b) => b.total_valor - a.total_valor)
          .slice(0, 10);

        setExpensesData(expensesArray);
      }
    } catch (err) {
      setError("Erro ao carregar dados dos gráficos");
      console.error("Error loading chart data:", err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-PT", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <Paper sx={{ p: 1.5 }}>
          <Typography variant="body2" fontWeight="bold">
            {label}
          </Typography>
          {payload.map((entry: any, index: number) => {
            // Formatar como moeda se for total_valor ou se o nome incluir "Valor"
            const isMonetaryValue =
              entry.dataKey === "total_valor" || entry.name.includes("Valor");
            const formattedValue = isMonetaryValue
              ? formatCurrency(entry.value)
              : entry.value;

            return (
              <Typography key={index} variant="body2" color={entry.color}>
                {entry.name}: {formattedValue}
              </Typography>
            );
          })}
        </Paper>
      );
    }
    return null;
  };

  return (
    <Box>
      {/* Filtros */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Filtros
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <FormControl fullWidth>
            <InputLabel>Ano</InputLabel>
            <Select
              value={ano}
              label="Ano"
              onChange={(e: SelectChangeEvent<number>) =>
                setAno(Number(e.target.value))
              }
            >
              {years.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Mês</InputLabel>
            <Select
              value={mes}
              label="Mês"
              onChange={(e: SelectChangeEvent<number | "">) =>
                setMes(e.target.value === "" ? "" : Number(e.target.value))
              }
            >
              <MenuItem value="">
                <em>Ano inteiro</em>
              </MenuItem>
              {months.map((month) => (
                <MenuItem key={month.value} value={month.value}>
                  {month.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Top 10 Fornecedores com mais faturas */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Top 10 Fornecedores com Mais Faturas
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Fornecedores ordenados por quantidade de faturas emitidas
            </Typography>
            {suppliersData.length > 0 ? (
              <ResponsiveContainer width="100%" height={400}>
                <BarChart
                  data={suppliersData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 100 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="emitente_nome"
                    angle={-45}
                    textAnchor="end"
                    height={100}
                    interval={0}
                  />
                  <YAxis />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar
                    dataKey="total_invoices"
                    fill="#8884d8"
                    name="Número de Faturas"
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Typography variant="body2" color="text.secondary" align="center">
                Nenhum dado disponível para o período selecionado
              </Typography>
            )}
          </Paper>

          {/* Top 10 Despesas */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Top 10 Maiores Despesas por Fornecedor
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Fornecedores ordenados por valor total de despesas
            </Typography>
            {expensesData.length > 0 ? (
              <ResponsiveContainer width="100%" height={400}>
                <BarChart
                  data={expensesData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 100 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="emitente_nome"
                    angle={-45}
                    textAnchor="end"
                    height={100}
                    interval={0}
                  />
                  <YAxis />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar
                    dataKey="total_valor"
                    fill="#82ca9d"
                    name="Valor Total"
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Typography variant="body2" color="text.secondary" align="center">
                Nenhum dado disponível para o período selecionado
              </Typography>
            )}
          </Paper>

          {/* Gráfico de Pizza - Distribuição de Despesas */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Distribuição de Despesas por Fornecedor
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Percentual de despesas por fornecedor (Top 10)
            </Typography>
            {expensesData.length > 0 ? (
              <ResponsiveContainer width="100%" height={400}>
                <PieChart>
                  <Pie
                    data={expensesData as any}
                    dataKey="total_valor"
                    nameKey="emitente_nome"
                    cx="50%"
                    cy="50%"
                    outerRadius={120}
                    label={(entry: any) =>
                      `${entry.emitente_nome}: ${formatCurrency(
                        entry.total_valor
                      )}`
                    }
                  >
                    {expensesData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Typography variant="body2" color="text.secondary" align="center">
                Nenhum dado disponível para o período selecionado
              </Typography>
            )}
          </Paper>
        </>
      )}
    </Box>
  );
};

export default ChartsView;
